// Таблица выплат: её страницы и суммы на них.
//
// Сумм игра не придумывает. Символы и таблица приходят с бэкенда описанием игры, а под ставку
// игрока они пересчитываются тем же счётом, каким это делает бэкенд, см. ADR-42 и scalePayout.
// Разойдись эти два счёта — таблица обещала бы игроку не ту сумму, которую он получит.
//
// Ставку игрок двигает между раундами, поэтому таблица её слушает: суммы переписываются
// на месте, а страницы остаются те же. Иначе игрок открыл бы таблицу и прочитал выплаты
// от ставки, которой уже не играет.
//
// Страниц три: выплаты символов поля, линии выплат и выплаты мини-слота. Мини-слот платит
// по своей доске и по своей таблице, поэтому его страница строится из неё, а не из главной.

import { Container, Sprite, Text, type TextStyleOptions } from 'pixi.js';

import { baseStake, boardOf, scalePayout, type Cents, type GameConfig, type SymbolId } from '../api/gameConfig';
import { PAYLINES_IMAGE, TITLE_FONT } from '../assets/catalog';
import type { AssetManager } from '../assets/manager';
import type { SymbolLibrary } from '../game/symbols';
import { text, textFamily } from '../i18n/text';

import { moneyText } from './amount';
import { fitLabel } from './label';
import { pageBody, pageHeading, pageNote, CONTENT_Y, NOTE_GAP, PAGE_TOP, PAGE_WIDTH } from './popup';

/** Сколько символов стоит в ряду сетки выплат. */
const COLUMNS: number = 3;

/** Клетка сетки: символ слева, суммы справа. */
const CELL_WIDTH: number = PAGE_WIDTH / COLUMNS;
const CELL_HEIGHT: number = 148;

/** Высота символа в клетке. Кадр символа 204x170, поэтому по ширине он выходит около 120. */
const SYMBOL_HEIGHT: number = 100;
const SYMBOL_WIDTH: number = (SYMBOL_HEIGHT * 204) / 170;

/** Середина символа и левый край сумм в клетке. */
const SYMBOL_X: number = -68;
const PAYOUT_X: number = 2;

/**
 * Докуда суммам расти вправо: до символа соседней клетки, а в крайней клетке — до края страницы.
 *
 * На верхней ступени лестницы ставок суммы доходят до четырёх знаков, и без этой рамки
 * «3   €1,111.11» упёрлось бы в соседний символ, см. ADR-33.
 */
const PAYOUT_RIGHT: number = Math.min(CELL_WIDTH + SYMBOL_X - SYMBOL_WIDTH / 2, PAGE_WIDTH / 2 - ((COLUMNS - 1) / 2) * CELL_WIDTH);

/** Сколько места остаётся суммам в клетке. */
const PAYOUT_WIDTH: number = PAYOUT_RIGHT - PAYOUT_X;

/** Размер сумм в клетке сетки. */
const PAYOUT_SIZE: number = 24;

/** Суммы в клетке: слева количество символов, справа выплата. */
function payoutStyle(family: string): TextStyleOptions {
  return {
    fontFamily: textFamily(family),
    fontSize: PAYOUT_SIZE,
    fontWeight: 'bold',
    fill: 0xfff4dd,
    align: 'left',
    lineHeight: PAYOUT_SIZE * 1.4,
  };
}

/** Выплаты одного символа на странице: надпись с суммами и таблица, по которой она пишется. */
interface PaidSymbol {
  /** Выплаты символа на базовой ставке: количество символов в комбинации -> сумма. */
  readonly payouts: Readonly<Record<number, Cents>>;

  /** Базовая ставка доски, с которой сумма переводится на ставку игрока. */
  readonly base: Cents;

  /** Надпись с суммами. Её и переписывает смена ставки. */
  readonly label: Text;
}

/**
 * Символы доски, за которые она платит, от дорогого к дешёвому.
 *
 * Символ без выплат в таблицу не идёт: показывать игроку строку без сумм незачем.
 * Порядок берётся по самой длинной комбинации символа.
 */
function paidIds(board: GameConfig): SymbolId[] {
  const top = new Map<SymbolId, Cents>();

  for (const symbol of board.symbols) {
    const payouts: Readonly<Record<number, Cents>> | undefined = board.paytable[symbol.id];
    if (payouts === undefined)
      continue;

    const counts: number[] = Object.keys(payouts).map(Number);
    if (counts.length > 0)
      top.set(symbol.id, payouts[Math.max(...counts)]);
  }

  return [...top.keys()].sort((left: SymbolId, right: SymbolId) => (top.get(right) ?? 0) - (top.get(left) ?? 0));
}

/** Записывает выплаты символа: строка на каждую длину комбинации, от длинной к короткой. */
function payoutText(payouts: Readonly<Record<number, Cents>>, stake: Cents, base: Cents): string {
  const counts: number[] = Object.keys(payouts)
    .map(Number)
    .sort((left: number, right: number) => right - left);

  return counts.map((count: number) => `${count}   ${moneyText(scalePayout(payouts[count], stake, base))}`).join('\n');
}

/** Таблица выплат игры. */
export class Paytable {
  /** Страницы таблицы. Их показывает попап, см. ui/popup.ts. */
  readonly pages: readonly Container[];

  /** Все надписи с суммами: их переписывает смена ставки. */
  readonly #paid: PaidSymbol[] = [];

  readonly #assets: AssetManager;
  readonly #symbols: SymbolLibrary;
  readonly #family: string;

  #linesHeading: Text | null = null;
  #linesNote: Text | null = null;
  #bonusHeading: Text | null = null;
  #bonusNote: Text | null = null;

  #stake: Cents;

  /**
   * @param stake Ставка, под которую написаны суммы на этот миг.
   */
  constructor(config: GameConfig, symbols: SymbolLibrary, assets: AssetManager, stake: Cents) {
    this.#assets = assets;
    this.#symbols = symbols;
    this.#family = assets.font(TITLE_FONT);
    this.#stake = stake;

    const pages: Container[] = [this.#symbolsPage(config), this.#linesPage()];

    // Мини-слот платит по своей доске. Слот без фичи её не присылает, и страницы про неё нет.
    const bonus: string | undefined = config.steamSpin?.bonusBoard;
    if (bonus !== undefined)
      pages.push(this.#bonusPage(config, boardOf(config, bonus)));

    this.pages = pages;
  }

  /** Ставка, под которую написаны суммы. */
  get stake(): Cents {
    return this.#stake;
  }

  /** Переписывает суммы под новую ставку. Та же ставка — работы нет. */
  set stake(cents: Cents) {
    if (cents === this.#stake)
      return;

    this.#stake = cents;

    for (const paid of this.#paid) {
      paid.label.text = payoutText(paid.payouts, cents, paid.base);
      fitLabel(paid.label, { width: PAYOUT_WIDTH });
    }
  }

  /** Переписывает локализованные подписи, сохраняя страницы и таблицы выплат. */
  refreshText(): void {
    if (this.#linesHeading !== null) {
      this.#linesHeading.text = text('linesTitle');
      fitLabel(this.#linesHeading, { width: PAGE_WIDTH });
    }

    if (this.#linesNote !== null) {
      this.#linesNote.text = text('linesActive');
      fitLabel(this.#linesNote, { width: PAGE_WIDTH });
    }

    if (this.#bonusHeading !== null) {
      this.#bonusHeading.text = text('steamSpinName');
      fitLabel(this.#bonusHeading, { width: PAGE_WIDTH });
    }

    if (this.#bonusNote !== null) {
      this.#bonusNote.text = text('steamSpinBonusRules');
      fitLabel(this.#bonusNote, { width: PAGE_WIDTH });
    }
  }

  /** Страница выплат символов главного поля. Заголовка у неё нет: его несёт плашка попапа. */
  #symbolsPage(config: GameConfig): Container {
    const page = new Container();
    page.addChild(this.#grid(paidIds(config), config, baseStake(config), PAGE_TOP + 5));

    return page;
  }

  /** Страница линий выплат: девять картинок поля и строка о том, что все они всегда играют. */
  #linesPage(): Container {
    const page = new Container();
    const heading = pageHeading(text('linesTitle'), this.#family, this.#assets);
    const body = pageBody(this.#assets.texture(PAYLINES_IMAGE), text('linesActive'), this.#family);

    this.#linesHeading = heading.children[0] as Text;
    this.#linesNote = body.children[1] as Text;
    page.addChild(heading, body);

    return page;
  }

  /**
   * Страница выплат мини-слота.
   *
   * Суммы его доски переводятся базовой ставкой главного поля, а не своей: мини-слот платит
   * за тот же спин, и своих линий игрок не выбирает. Так же считает и бэкенд, см. GameConfig.Scaled.
   */
  #bonusPage(config: GameConfig, board: GameConfig): Container {
    const page = new Container();
    const ids: SymbolId[] = paidIds(board);

    // Над надписью здесь стоит сетка, а не иллюстрация, и подвинуться она не может: клетки
    // стоят рядами по своей высоте. Поэтому надпись поднимается только до низа сетки.
    const bottom: number = CONTENT_Y + Math.ceil(ids.length / COLUMNS) * CELL_HEIGHT;

    const heading = pageHeading(text('steamSpinName'), this.#family, this.#assets);
    const note = pageNote(text('steamSpinBonusRules'), this.#family, bottom + NOTE_GAP);
    this.#bonusHeading = heading.children[0] as Text;
    this.#bonusNote = note;
    page.addChild(heading, this.#grid(ids, board, baseStake(config), CONTENT_Y), note);

    return page;
  }

  /** Сетка выплат доски: символы идут слева направо и сверху вниз. */
  #grid(ids: readonly SymbolId[], board: GameConfig, base: Cents, top: number): Container {
    const view = new Container();

    for (let index = 0; index < ids.length; index++) {
      const column: number = index % COLUMNS;
      const row: number = Math.floor(index / COLUMNS);

      const cell: Container = this.#cell(board, ids[index], base);
      cell.position.set((column - (COLUMNS - 1) / 2) * CELL_WIDTH, top + (row + 0.5) * CELL_HEIGHT);

      view.addChild(cell);
    }

    return view;
  }

  /** Клетка сетки: символ и его выплаты рядом. */
  #cell(board: GameConfig, id: SymbolId, base: Cents): Container {
    const view = new Container();
    const payouts: Readonly<Record<number, Cents>> = board.paytable[id];

    const picture = new Sprite(this.#symbols.still(id));
    picture.anchor.set(0.5);
    picture.scale.set(SYMBOL_HEIGHT / picture.texture.height);
    picture.position.set(SYMBOL_X, 0);

    const label = new Text({ text: payoutText(payouts, this.#stake, base), style: payoutStyle(this.#family) });
    label.anchor.set(0, 0.5);
    label.position.set(PAYOUT_X, 0);
    fitLabel(label, { width: PAYOUT_WIDTH });

    this.#paid.push({ payouts, base, label });

    view.addChild(picture, label);

    return view;
  }
}
